<script setup lang="ts">
const localePath = useLocalePath()
const { t } = useI18n()
const {
  public: { baseUrl },
} = useRuntimeConfig()

const pageMeta = usePageMeta()

const { defineOgMeta } = useSeoAttributes()

const { pending, data } = await useAsyncData('meta-services', async () => pageMeta.fetchByPath('/services'))

if (data.value) {
  useHead(() => ({
    title: data.value?.title,
    meta: [...defineOgMeta(data?.value)],
    link: [{ rel: 'canonical', href: `${baseUrl}/services` }],
  }))
}

const info = {
  landing: {
    title: t('services.title'),
    description: t('services.description'),
  },
  desktop: {
    title: t('services.desktop.title'),
    description: t('services.desktop.description'),
    buttonLabel: t('common.see_how_it_works'),
    buttonLink: '',
    imagePath: 'services-desktop.svg',
  },
  mobile: {
    title: t('services.mobile.title'),
    description: t('services.mobile.description'),
    buttonLabel: t('common.see_how_it_works'),
    buttonLink: '',
    imagePath: 'services-mobile.svg',
  },
  connect: {
    eyebrow: t('services.connect.eyebrow'),
    title: t('services.connect.title'),
    description: t('services.connect.description'),
    imagePath: 'connect.svg',
  },
}
</script>

<template>
  <Loader :pending="pending">
    <Container class="container overflow-x-hidden px-6 py-32 md:px-14 lg:px-16">
      <Heading size="large" align="center" gradient="gray" class="mb-8"> {{ info.landing.title }} </Heading>

      <div class="relative flex flex-col items-center justify-center">
        <div
          :class="[
            'xs:top-[-6rem] absolute top-[-12.25rem] md:-top-24',
            'bg-primary-background xs:h-[58rem] xs:w-screen lg:h-[48rem] xl:w-[100rem]',
            '-z-10 rounded-3xl',
          ]"
        />
        <div class="absolute z-0 sm:right-16 md:top-44 lg:right-44 lg:top-14">
          <img
            src="/services/services-background-cubes.svg"
            alt="services-background-cubes"
            class="xs:hidden md:block lg:w-[1400px]"
          />
        </div>
        <div class="absolute left-[-3rem] top-[14rem] lg:right-44">
          <img
            src="/services/services-background-cubes-sm.svg"
            alt="services-background-cubes"
            class="xs:block sm:hidden"
          />
        </div>
        <div class="z-10 mb-24 flex flex-col items-center justify-center gap-y-8">
          <Paragraph size="large" class="max-w-lg text-center">
            {{ info.landing.description }}
          </Paragraph>
          <div class="xs:mb-52 z-50 lg:mb-[0]">
            <Action as="link" size="lg" :to="localePath('/projects')" intent="main">
              {{ $t('services.ctaButton') }}
            </Action>
          </div>
          <div class="xs:flex-col xs:gap-16 flex justify-between md:flex-row lg:mt-24 xl:gap-[26rem]">
            <DevicesInfo class="w-full" :data="info.desktop" />
            <DevicesInfo class="w-full" :data="info.mobile" />
          </div>
          <div>
            <WeOffer />
          </div>
          <div class="relative">
            <Connect :data="info.connect" />
          </div>
        </div>
      </div>

      <div class="mt-[20rem] flex w-full flex-wrap justify-center gap-60 lg:gap-32 xl:gap-60">
        <WBottomCard
          :title="$t('projects.title')"
          :eyebrow="$t('common.card.projects.eyebrow')"
          section-link="/projects"
          image-link="../art-projects.svg"
        />
        <WBottomCard
          :title="$t('services.card.contact.eyebrow')"
          :eyebrow="$t('services.card.contact.eyebrow')"
          section-link="/contact"
          image-link="../art-contact.svg"
        />
      </div>
    </Container>
  </Loader>
</template>
